import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Truck, Lock, Envelope, Warning } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const BG = "https://images.unsplash.com/photo-1655478157959-3fcfc543c1a2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwxfHxjYXJnbyUyMHRydWNrJTIwc3Vuc2V0JTIwaGlnaHdheXxlbnwwfHx8fDE3ODMyNTMyMjR8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login, formatApiError } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try {
      await login(email.trim(), password);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex">
      <div className="hidden lg:block lg:w-1/2 relative">
        <img src={BG} alt="Cargo truck on highway" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-slate-900/80" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="bg-white rounded-lg px-4 py-3 inline-flex items-center w-max">
            <img src="/cargodash-logo.png" alt="CargoDash" className="h-8 w-auto" />
          </div>
          <div>
            <h1 className="font-heading font-bold text-4xl leading-tight tracking-tight mb-3">
              Dedicated vehicle quoting,<br />costed to the cent.
            </h1>
            <p className="text-slate-300 max-w-md">
              Route mapping, an authoritative costing engine, quote revisions, and conflict-checked trip scheduling — built for your dispatch desk.
            </p>
          </div>
          <div className="text-xs text-slate-400 font-mono">Africa/Johannesburg · ZAR · 15% VAT</div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-sm">
          <div className="lg:hidden mb-8">
            <img src="/cargodash-logo.png" alt="CargoDash" className="h-9 w-auto" />
          </div>
          <h2 className="font-heading font-bold text-2xl tracking-tight text-slate-900">Sign in</h2>
          <p className="text-sm text-slate-500 mb-8">Internal access only. Contact an administrator for an account.</p>

          {error && (
            <div className="mb-4 flex items-start gap-2 bg-red-50 border-l-4 border-red-500 p-3 rounded-r-md text-red-800 text-sm" data-testid="login-error">
              <Warning size={18} weight="fill" className="mt-0.5 shrink-0" />{error}
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email</label>
              <div className="relative mt-1">
                <Envelope size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input data-testid="login-email" type="email" required value={email}
                  onChange={(e) => setEmail(e.target.value)} autoComplete="username"
                  className="w-full pl-10 pr-3 py-2.5 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none"
                  placeholder="you@company.co.za" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Password</label>
              <div className="relative mt-1">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input data-testid="login-password" type="password" required value={password}
                  onChange={(e) => setPassword(e.target.value)} autoComplete="current-password"
                  className="w-full pl-10 pr-3 py-2.5 border border-slate-300 rounded-md text-sm focus:ring-2 focus:ring-slate-900 focus:border-slate-900 outline-none"
                  placeholder="••••••••" />
              </div>
            </div>
            <Button data-testid="login-submit" type="submit" disabled={busy}
              className="w-full bg-slate-900 hover:bg-slate-800 text-white h-11">
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
